import React, { useState, useEffect, useCallback } from 'react';
import { Input, Separator } from '@roger/ui-components';
import { GripVertical, Loader2, X } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  listVisitDiagnosesVisitsVisitIdDiagnosesGet,
  createVisitDiagnosisVisitsVisitIdDiagnosesPost,
  updateVisitDiagnosisVisitsDiagnosesDiagnosisIdPut,
  deleteVisitDiagnosisVisitsDiagnosesDiagnosisIdDelete,
  updateDiagnosisPositionsVisitsVisitIdDiagnosesPositionsPut,
} from '../../services/api/sdk.gen';
import type { VisitDiagnosisResponse } from '../../services/api/types.gen';
import DraggableGrid from '../DraggableGrid';
import InlineDiagnosisSearch from './InlineDiagnosisSearch';
import InlineDiagnosisFields from './InlineDiagnosisFields';
import { useToast } from '../../hooks/useToastContext';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { OASISActiveDiagnoses } from './OASISActiveDiagnoses';
import { parseISOToLocalDate, formatDateToISO } from '../../utils/dateUtils';
import type { OEType, SeverityType } from '../../types/codingData.types';

const MAX_TOTAL = 25;

interface VisitActiveDiagnosisProps {
  visitId: string;
}

interface SortableRowProps {
  id: string;
  children: React.ReactNode;
}

const SortableRow: React.FC<SortableRowProps> = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : ('auto' as const),
  };

  return (
    <div ref={setNodeRef} style={style} className='group'>
      <div className='flex items-start gap-2 py-2 px-3 rounded-lg border bg-white'>
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className='mt-1 cursor-grab active:cursor-grabbing p-0.5 hover:bg-muted rounded shrink-0'
          title='Drag to reorder'
        >
          <GripVertical className='w-4 h-4 text-muted-foreground' />
        </div>
        <div className='flex-1 min-w-0'>{children}</div>
      </div>
    </div>
  );
};

export const VisitActiveDiagnosis: React.FC<VisitActiveDiagnosisProps> = ({
  visitId,
}) => {
  const { error: showError } = useToast();
  const [diagnoses, setDiagnoses] = useState<VisitDiagnosisResponse[]>([]);
  const [loading, setLoading] = useState(true);
  // Track temp IDs for optimistic creates
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  const fetchDiagnoses = useCallback(async () => {
    try {
      const response = await listVisitDiagnosesVisitsVisitIdDiagnosesGet({
        path: { visit_id: visitId },
      });
      if (response.data) {
        setDiagnoses(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch visit diagnoses:', error);
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    fetchDiagnoses();
  }, [fetchDiagnoses]);

  const allDiagnoses = [...diagnoses].sort((a, b) => a.position - b.position);

  const isAtLimit = diagnoses.length >= MAX_TOTAL;

  // --- Handlers ---

  const handleFieldChange = async (
    diagnosisId: string,
    fields: {
      oe?: OEType;
      onset_date?: string | null;
      severity?: SeverityType;
    }
  ) => {
    // Build update payload, coercing null OE to empty string for backend's non-nullable `oe: str`
    const update: Record<string, unknown> = {};
    if (fields.oe !== undefined) update.oe = fields.oe ?? '';
    if (fields.onset_date !== undefined) update.onset_date = fields.onset_date;
    if (fields.severity !== undefined) update.severity = fields.severity;

    setDiagnoses(prev =>
      prev.map(d =>
        d.id === diagnosisId
          ? ({ ...d, ...update } as VisitDiagnosisResponse)
          : d
      )
    );

    if (pendingIds.has(diagnosisId)) return; // Don't send API for temp items

    try {
      await updateVisitDiagnosisVisitsDiagnosesDiagnosisIdPut({
        path: { diagnosis_id: diagnosisId },
        body: update,
      });
    } catch (error) {
      console.error('Failed to update diagnosis:', error);
      fetchDiagnoses();
    }
  };

  const handleUpdateCode = async (
    diagnosisId: string,
    code: string,
    description: string
  ) => {
    setDiagnoses(prev =>
      prev.map(d => (d.id === diagnosisId ? { ...d, code, description } : d))
    );

    if (pendingIds.has(diagnosisId)) return;

    try {
      await updateVisitDiagnosisVisitsDiagnosesDiagnosisIdPut({
        path: { diagnosis_id: diagnosisId },
        body: { code, description },
      });
    } catch (error) {
      console.error('Failed to update diagnosis code:', error);
      fetchDiagnoses();
    }
  };

  const handleAddDiagnosis = async (code: string, description: string) => {
    if (isAtLimit) {
      showError(`Maximum of ${MAX_TOTAL} diagnoses allowed per visit.`);
      return;
    }

    const hasPrimary = diagnoses.some(d => d.is_primary);
    const isPrimary = !hasPrimary;
    const position = isPrimary
      ? 0
      : Math.max(...diagnoses.map(d => d.position), -1) + 1;
    const tempId = crypto.randomUUID();

    // Optimistic add
    const tempDiagnosis: VisitDiagnosisResponse = {
      id: tempId,
      code,
      description,
      oe: '',
      onset_date: null,
      severity: null,
      is_primary: isPrimary,
      position,
      agency_id: '',
      visit_id: visitId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setDiagnoses(prev => [...prev, tempDiagnosis]);
    setPendingIds(prev => new Set(prev).add(tempId));

    try {
      const response = await createVisitDiagnosisVisitsVisitIdDiagnosesPost({
        path: { visit_id: visitId },
        body: {
          code,
          description,
          oe: '',
          onset_date: null,
          severity: null,
          is_primary: isPrimary,
          position,
        },
      });
      if (response.data) {
        // Replace temp with real
        setDiagnoses(prev =>
          prev.map(d => (d.id === tempId ? response.data! : d))
        );
      } else {
        // Remove temp on error
        setDiagnoses(prev => prev.filter(d => d.id !== tempId));
        showError(
          getUserFacingApiMessage(response, 'Failed to create diagnosis')
        );
      }
    } catch (error) {
      console.error('Failed to create diagnosis:', error);
      setDiagnoses(prev => prev.filter(d => d.id !== tempId));
      showError('Failed to create diagnosis');
    } finally {
      setPendingIds(prev => {
        const next = new Set(prev);
        next.delete(tempId);
        return next;
      });
    }
  };

  const handleRemoveDiagnosis = async (diagnosisId: string) => {
    setDiagnoses(prev => prev.filter(d => d.id !== diagnosisId));

    if (pendingIds.has(diagnosisId)) return;

    try {
      await deleteVisitDiagnosisVisitsDiagnosesDiagnosisIdDelete({
        path: { diagnosis_id: diagnosisId },
      });
    } catch (error) {
      console.error('Failed to delete diagnosis:', error);
      fetchDiagnoses();
    }
  };

  const handleReorder = async (reordered: VisitDiagnosisResponse[]) => {
    const updatedWithPositions = reordered.map((d, idx) => ({
      ...d,
      position: idx,
    }));

    const newTop = updatedWithPositions[0];
    const primaryChanged = newTop && !newTop.is_primary;

    if (primaryChanged) {
      const withPrimary = updatedWithPositions.map(d => ({
        ...d,
        is_primary: d.id === newTop.id,
      }));
      setDiagnoses(withPrimary);
    } else {
      setDiagnoses(updatedWithPositions);
    }

    try {
      await updateDiagnosisPositionsVisitsVisitIdDiagnosesPositionsPut({
        path: { visit_id: visitId },
        body: updatedWithPositions.map(d => ({
          id: d.id,
          position: d.position,
          ...(primaryChanged ? { is_primary: d.id === newTop.id } : {}),
        })),
      });
    } catch (error) {
      console.error('Failed to update positions:', error);
      fetchDiagnoses();
    }
  };

  // --- Rendering ---

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='w-6 h-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div
        className='flex flex-col gap-2'
        data-contradiction-source='diagnosis_list'
      >
        {/* Header */}
        <div className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
          Diagnoses
        </div>

        {/* All diagnosis rows (draggable) */}
        {allDiagnoses.length > 0 && (
          <DraggableGrid
            items={allDiagnoses}
            onReorder={handleReorder}
            renderItem={dx => (
              <SortableRow key={dx.id} id={`dx-${dx.id}`}>
                <DiagnosisRowContent
                  diagnosis={dx}
                  onFieldChange={fields => handleFieldChange(dx.id, fields)}
                  onUpdateCode={(code, desc) =>
                    handleUpdateCode(dx.id, code, desc)
                  }
                  onRemove={() => handleRemoveDiagnosis(dx.id)}
                  excludeCodes={diagnoses
                    .filter(d => d.id !== dx.id)
                    .map(d => d.code)}
                />
              </SortableRow>
            )}
            getItemId={dx => `dx-${dx.id}`}
            gridClassName='flex flex-col gap-2'
            sortingStrategy='vertical'
          />
        )}

        {/* Ghost slot */}
        {!isAtLimit && (
          <div className='flex items-start gap-2 py-2 px-3 rounded-lg border border-dashed bg-white'>
            {/* Spacer for drag handle alignment */}
            <div className='w-5 shrink-0' />
            <div className='flex-1 min-w-0'>
              <div className='flex flex-col gap-1.5'>
                <div className='flex items-center gap-2'>
                  <InlineDiagnosisSearch
                    onSelect={handleAddDiagnosis}
                    placeholder={
                      !diagnoses.some(d => d.is_primary)
                        ? 'Search to add primary diagnosis...'
                        : 'Search to add diagnosis...'
                    }
                    excludeCodes={diagnoses.map(d => d.code)}
                  />
                </div>
                <InlineDiagnosisFields disabled />
              </div>
            </div>
          </div>
        )}
      </div>

      <Separator />

      {/* OASIS Active Diagnoses Comorbidities */}
      <OASISActiveDiagnoses visitId={visitId} />
    </div>
  );
};

// --- Row components ---

interface DiagnosisRowContentProps {
  diagnosis: VisitDiagnosisResponse;
  onFieldChange: (fields: {
    oe?: OEType;
    onset_date?: string | null;
    severity?: SeverityType;
  }) => void;
  onUpdateCode: (code: string, description: string) => void;
  onRemove?: () => void;
  excludeCodes?: string[];
}

const DiagnosisRowContent: React.FC<DiagnosisRowContentProps> = ({
  diagnosis,
  onFieldChange,
  onUpdateCode,
  onRemove,
  excludeCodes,
}) => {
  const onsetDate = parseISOToLocalDate(diagnosis.onset_date) ?? null;

  return (
    <div className='flex flex-col gap-1.5'>
      {/* Top line: label/position + code badge + search + remove */}
      <span
        className={`text-[10px] font-semibold uppercase tracking-wide shrink-0 ${
          diagnosis.is_primary ? 'text-primary' : 'text-muted-foreground'
        }`}
      >
        {diagnosis.is_primary ? 'Primary' : 'Secondary'}
      </span>
      <div className='flex items-center gap-2'>
        <InlineDiagnosisSearch
          description={diagnosis.description}
          onSelect={onUpdateCode}
          excludeCodes={excludeCodes}
        />

        <Input
          className='font-mono w-fit max-w-32 text-center bg-primary-500 text-white'
          value={diagnosis.code}
          readOnly
        />

        {onRemove && (
          <button
            type='button'
            onClick={onRemove}
            className='p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors shrink-0'
            title='Remove diagnosis'
          >
            <X className='w-4 h-4' />
          </button>
        )}
      </div>

      {/* Bottom line: O/E, Date, Severity fields */}
      <InlineDiagnosisFields
        oe={(diagnosis.oe as OEType) || null}
        onsetDate={onsetDate}
        onsetDateRaw={diagnosis.onset_date}
        severity={(diagnosis.severity as SeverityType) || null}
        onOEChange={oe => onFieldChange({ oe })}
        onOnsetDateChange={date =>
          onFieldChange({
            onset_date: date ? formatDateToISO(date) : null,
          })
        }
        onSeverityChange={severity => onFieldChange({ severity })}
      />
    </div>
  );
};

export default VisitActiveDiagnosis;
